import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export type DashboardBooking = {
  id: string
  client_name: string
  duration: number
  scheduled_at: string
  service_name: string
  service_price: number
  staff_name: string
  status: string
  updated_at: string
}

export type DashboardData = {
  todayBookings: DashboardBooking[]
  metrics: {
    todayCount: number
    pending: number
    completed: number
    monthlyRevenue: number
    activeClients: number
    avgBookingValue: number
  }
  feed: DashboardBooking[]
  topServices: { name: string; count: number }[]
}

export const EMPTY_DASHBOARD_DATA: DashboardData = {
  todayBookings: [],
  metrics: {
    todayCount: 0,
    pending: 0,
    completed: 0,
    monthlyRevenue: 0,
    activeClients: 0,
    avgBookingValue: 0,
  },
  feed: [],
  topServices: [],
}

export function formatDashboardTime12(dateString: string) {
  return new Date(dateString).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

export function formatDashboardMoney(n: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)
}

export function useAdminDashboardData(tenantId?: string) {
  return useQuery({
    queryKey: ['dashboard-overview-v2', tenantId],
    queryFn: async (): Promise<DashboardData> => {
      if (!tenantId) return EMPTY_DASHBOARD_DATA

      const now = new Date()
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString()
      const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toISOString()
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
      const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString()

      // Today's bookings + month bookings (for revenue) in parallel
      const [todayRes, monthRes, clientsRes] = await Promise.all([
        supabase
          .from('bookings')
          .select(
            'id, client_name, duration, scheduled_at, service_id, stylist_id, status, updated_at'
          )
          .eq('tenant_id', tenantId)
          .gte('scheduled_at', todayStart)
          .lt('scheduled_at', todayEnd)
          .order('scheduled_at', { ascending: true }),
        supabase
          .from('bookings')
          .select('id, service_id, status')
          .eq('tenant_id', tenantId)
          .gte('scheduled_at', monthStart)
          .in('status', ['confirmed', 'completed']),
        supabase
          .from('tenant_clients')
          .select('id', { count: 'exact', head: true })
          .eq('tenant_id', tenantId)
          .gte('created_at', ninetyDaysAgo),
      ])

      const todayRows = todayRes.data ?? []
      const monthRows = monthRes.data ?? []

      // Collect all service IDs
      const allServiceIds = Array.from(
        new Set([
          ...(todayRows.map((b) => b.service_id).filter(Boolean) as string[]),
          ...(monthRows.map((b) => b.service_id).filter(Boolean) as string[]),
        ])
      )
      const stylistIds = Array.from(
        new Set(todayRows.map((b) => b.stylist_id).filter(Boolean) as string[])
      )

      const [servicesRes, stylistsRes] = await Promise.all([
        allServiceIds.length > 0
          ? supabase.from('services').select('id, name, price').in('id', allServiceIds)
          : Promise.resolve({ data: [], error: null }),
        stylistIds.length > 0
          ? supabase
              .from('stylists')
              .select('id, name')
              .eq('tenant_id', tenantId)
              .in('id', stylistIds)
          : Promise.resolve({ data: [], error: null }),
      ])

      const serviceMap = new Map((servicesRes.data ?? []).map((s) => [s.id, s]))
      const stylistMap = new Map((stylistsRes.data ?? []).map((s) => [s.id, s.name]))

      // Enrich today's bookings
      const todayEnriched: DashboardBooking[] = todayRows.map((b) => ({
        id: b.id,
        client_name: b.client_name,
        duration: b.duration,
        scheduled_at: b.scheduled_at,
        service_name: b.service_id ? (serviceMap.get(b.service_id)?.name ?? 'Service') : 'Service',
        service_price: b.service_id ? (serviceMap.get(b.service_id)?.price ?? 0) : 0,
        staff_name: b.stylist_id ? (stylistMap.get(b.stylist_id) ?? 'Unassigned') : 'Unassigned',
        status: b.status,
        updated_at: b.updated_at,
      }))

      // Revenue from this month's bookings via service price
      const monthlyRevenue = monthRows.reduce((sum, b) => {
        const price = b.service_id ? (serviceMap.get(b.service_id)?.price ?? 0) : 0
        return sum + price
      }, 0)

      // Top services by booking count (this month)
      const serviceCounts = new Map<string, { name: string; count: number }>()
      monthRows.forEach((b) => {
        if (!b.service_id) return
        const svc = serviceMap.get(b.service_id)
        if (!svc) return
        const existing = serviceCounts.get(b.service_id)
        serviceCounts.set(b.service_id, { name: svc.name, count: (existing?.count ?? 0) + 1 })
      })
      const topServices = [...serviceCounts.values()].sort((a, b) => b.count - a.count).slice(0, 4)

      const completedCount = todayEnriched.filter((b) => b.status === 'completed').length
      const avgBookingValue = monthRows.length > 0 ? monthlyRevenue / monthRows.length : 0

      return {
        todayBookings: todayEnriched,
        metrics: {
          todayCount: todayEnriched.length,
          pending: todayEnriched.filter((b) => b.status === 'pending').length,
          completed: completedCount,
          monthlyRevenue,
          activeClients: clientsRes.count ?? 0,
          avgBookingValue,
        },
        feed: [...todayEnriched]
          .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
          .slice(0, 5),
        topServices,
      }
    },
    enabled: !!tenantId,
  })
}
